import { useEffect, useState } from "react";

// STYLES
import * as Style from "./RentalDetailsStyle";

// LIBRARIES
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc";
import { useTranslation } from "react-i18next";
import { Box, Tab, Tabs } from "@mui/material";

// MISC
import { COLORS } from "constants/styleVariables";
import {
  ButtonTypes,
  ChildModalTypes,
  ModalTypes,
  NewcomeUserStatus
} from "models/GeneralTypes/generalTypes";
import { IRentalContent } from "models/interfaces";
import { IModalData, IProps } from "./RentalDetailsModel";

// ASSETS
import PersonDefaultIcon from "assets/Icons/defaultPersonPhoto.jpg";
import NoImagePlaceholder from "assets/Icons/No-Image-Placeholder.png";
import EditIconHovered from "assets/Icons/HistoryIconHovered.svg";

// REDUX
import { getRentalLog } from "api/auditApi";
import { getRentalDetails, updateRentalStatus } from "api/rentalApi";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import {
  modalProps,
  toggleChildModalState,
  toggleModalState
} from "slices/uiSlice";

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";

const RentalDetails = (props: IProps) => {
  // PROPS
  const { handleClose = () => { } } = props;

  // CONSTANTS USING LIBRARIES
  dayjs.extend(utcPlugin);
  const modalPropsData = useAppSelector<IModalData>(modalProps);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // CONSTANTS USING HOOKS
  const [statusColor, setStatusColor] = useState<string>("");
  const [rentalDetails, setRentalDetails] = useState<IRentalContent>();
  const [rentalLogsDetails, setRentalLogsDetails] = useState<any[]>();
  const [value, setValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // GENERAL CONSTANTS

  // USE EFFECT FUNCTION
  useEffect(() => {
    if (rentalDetails?.user?.userStatus === NewcomeUserStatus.VERIFIED) {
      setStatusColor(COLORS.primaryGreen);
    } else {
      setStatusColor(COLORS.terraCotta);
    }
    //eslint-disable-next-line
  }, [rentalDetails]);

  useEffect(() => {
    setIsLoading(true);
    if (modalPropsData.rental.rentalId || modalPropsData.rental.id) {
      dispatch(
        getRentalDetails(
          modalPropsData.rental.rentalId || modalPropsData.rental.id
        )
      )
        .unwrap()
        .then((response) => {
          setRentalDetails(response);
          setIsLoading(false);
        });
    }
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (modalPropsData.rental.rentalId || modalPropsData.rental.id) {
      dispatch(
        getRentalLog(modalPropsData.rental.rentalId || modalPropsData.rental.id)
      )
        .unwrap()
        .then((response) => {
          setRentalLogsDetails(response);
          setIsLoading(false);
        });
    }
    //eslint-disable-next-line
  }, []);
  // REQUEST API FUNCTIONS
  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 0 }}>
            {children}
          </Box>
        )}
      </div>
    );
  }

  // HANDLERS FUNCTIONS
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleOpenChildModal = (photo) => {
    if (photo) {
      dispatch(
        toggleChildModalState({
          isVisible: true,
          type: ChildModalTypes.EQUIPMENT,
          content: photo,
        })
      );
    }
  };

  const handleCloseToUser = () => {
    dispatch(
      toggleModalState({
        isVisible: true,
        type: ModalTypes.USER,
        rentalUser: modalPropsData.rentalUser,
        user: modalPropsData.user.user,
      })
    );
  };

  const handleUpdateRental = () => {
    const updateRentalPayload = {
      id: rentalDetails?.id,
      status: "RENTAL_FINISHED_BY_ADMIN",
    };
    dispatch(updateRentalStatus(updateRentalPayload))
      .unwrap()
      .then(() => {
        dispatch(
          getRentalLog(
            modalPropsData.rental.rentalId || modalPropsData.rental.id
          )
        )
          .unwrap()
          .then((response) => {
            setRentalLogsDetails(response);
          });
        dispatch(
          getRentalDetails(
            modalPropsData.rental.rentalId || modalPropsData.rental.id
          )
        )
          .unwrap()
          .then((response) => {
            setRentalDetails(response);
          });
      });
  };

  return (
    // <></>
    isLoading ?
      <Style.LoadingModalContainer>
        <Style.LodingSign />
      </Style.LoadingModalContainer> :
      <Style.ModalContainer>
        <Style.CloseIconContainer>
          <Style.CloseIcon
            onClick={modalPropsData.rentalUser ? handleCloseToUser : handleClose}
          />
        </Style.CloseIconContainer>



        <Style.HeaderWrapper>
          <Style.IconContainer>
            <Style.Icon
              src={EditIconHovered}

            >

            </Style.Icon>
          </Style.IconContainer>

          <Style.ContentInfo>
            <Style.Title>
              {t("modal.rentalDetails.rentDoor")}{" "}
              {rentalDetails?.slot?.identificationNumber || ""}
            </Style.Title>
            <Style.Subtitle>
              {t("modal.rentalDetails.locker")} {rentalDetails?.locker?.id || ""}
            </Style.Subtitle>
          </Style.ContentInfo>
        </Style.HeaderWrapper>

        <Style.UserInfoWrapper>
          <Style.UserImage src={modalPropsData.rental.image || PersonDefaultIcon} alt="" />
          <Style.UserInfo>
            <Style.UserName>{rentalDetails?.user?.username}</Style.UserName>
            <Style.UserStatus>
              <Style.Dot color={statusColor} />
              <Style.StatusValue color={statusColor}>
                {t(`user.status.${rentalDetails?.user?.userStatus}`) || ""}
              </Style.StatusValue>
            </Style.UserStatus>
          </Style.UserInfo>
          {!(
            rentalDetails?.status === "RENTAL_FINISHED" ||
            rentalDetails?.status === "RENTAL_FINISHED_BY_ADMIN"
          ) && (
              <ButtonAtom
                text={t("modal.rentalDetails.button.title")}
                type={ButtonTypes.SECONDARY_BUTTON_DEFAULT}
                onClick={() => handleUpdateRental()}
              />
            )}
        </Style.UserInfoWrapper>
        <Style.RentalInfoWrapper>
          <Style.LeftSideWrapper>
            <Style.RentalInfoLine>
              <Style.RentalInfoLabel>
                {t("modal.rentalDetails.date")}
              </Style.RentalInfoLabel>
              <Style.RentalInfoValue>
                {dayjs(rentalDetails?.startTime).format("DD/MM/YYYY") || ""}
              </Style.RentalInfoValue>
            </Style.RentalInfoLine>
            <Style.RentalInfoLine>
              <Style.RentalInfoLabel>
                {t("modal.rentalDetails.startTime")}
              </Style.RentalInfoLabel>
              <Style.RentalInfoValue>
                {dayjs(rentalDetails?.startTime).format("HH:mm:ss") || ""}
              </Style.RentalInfoValue>
            </Style.RentalInfoLine>
            <Style.RentalInfoLine>
              <Style.RentalInfoLabel>
                {t("modal.rentalDetails.endTime")}
              </Style.RentalInfoLabel>
              <Style.RentalInfoValue>
                {rentalDetails?.endTime != null
                  ? dayjs(rentalDetails?.endTime).format("HH:mm:ss")
                  : t("modal.rentalDetails.inProgress")}
              </Style.RentalInfoValue>
            </Style.RentalInfoLine>
            {!(
              rentalDetails?.status === "RENTAL_FINISHED" ||
              rentalDetails?.status === "RENTAL_FINISHED_BY_ADMIN"
            ) && (
                <Style.RentalInfoLine>
                  <Style.RentalInfoLabel>
                    {t("modal.rentalDetails.status")}
                  </Style.RentalInfoLabel>
                  <Style.RentalInfoValue>
                    {t(`audit.rentals.status.${rentalDetails?.status}`) || ""}
                  </Style.RentalInfoValue>
                </Style.RentalInfoLine>
              )}
          </Style.LeftSideWrapper>
          <Style.RightSideWrapper>
            <Style.RentalInfoLine>
              <Style.RentalInfoLabel>
                {t("modal.rentalDetails.duration")}
              </Style.RentalInfoLabel>
              <Style.RentalInfoValue>
                {rentalDetails?.endTime != null
                  ? dayjs
                    .utc(
                      dayjs(rentalDetails?.endTime).diff(
                        dayjs(rentalDetails?.startTime),
                        "second"
                      ) * 1000
                    )
                    .format("HH:mm:ss")
                  : ""}
                {rentalDetails?.endTime == null &&
                  t("modal.rentalDetails.inProgress")}
              </Style.RentalInfoValue>
            </Style.RentalInfoLine>
            <Style.RentalInfoLine>
              <Style.RentalInfoLabel>
                {t("modal.rentalDetails.amount")}
              </Style.RentalInfoLabel>
              <Style.RentalInfoValue>
                {(rentalDetails?.amount || "0") + " RON"}
              </Style.RentalInfoValue>
            </Style.RentalInfoLine>
          </Style.RightSideWrapper>
        </Style.RentalInfoWrapper>

        {rentalDetails?.isDoubleRental &&
          < Tabs
            value={value}
            onChange={handleChange}
            aria-label="slot tabs"
            variant="fullWidth"
          >
            <Tab value={0} label={`${t("modal.rentalDetails.slot")} ${rentalDetails?.slot?.identificationNumber}`} />
            <Tab value={1} label={`${t("modal.rentalDetails.slot")} ${rentalDetails?.secondSlot?.identificationNumber}`} />
          </Tabs>}

        <TabPanel value={value} index={0}>
          <Style.PhotosContainer>
            <Style.PhotoWrapper>
              <Style.EquipmentInfoTitle>
                {t("modal.rentalDetails.startRentingPhoto")}
              </Style.EquipmentInfoTitle>
              <Style.Photo
                src={rentalDetails?.startPhotoPath || ""}
                alt="Rental Start Photo"
                onClick={() => handleOpenChildModal(rentalDetails?.startPhotoPath)}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = NoImagePlaceholder;
                }}
              />
            </Style.PhotoWrapper>
            <Style.PhotoWrapper>
              <Style.EquipmentInfoTitle>
                {t("modal.rentalDetails.endRentingPhoto")}
              </Style.EquipmentInfoTitle>
              <Style.Photo
                src={rentalDetails?.endPhotoPath || ""}
                alt="Rental End Photo"
                onClick={() => handleOpenChildModal(rentalDetails?.endPhotoPath)}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = NoImagePlaceholder;
                }}
              />
            </Style.PhotoWrapper>
          </Style.PhotosContainer>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Style.PhotosContainer>
            <Style.PhotoWrapper>
              <Style.EquipmentInfoTitle>
                {t("modal.rentalDetails.startRentingPhoto")}
              </Style.EquipmentInfoTitle>
              <Style.Photo
                src={rentalDetails?.startPhotoPathSecond || ""}
                alt="Rental Start Photo"
                onClick={() => handleOpenChildModal(rentalDetails?.startPhotoPathSecond)}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = NoImagePlaceholder;
                }}
              />
            </Style.PhotoWrapper>
            <Style.PhotoWrapper>
              <Style.EquipmentInfoTitle>
                {t("modal.rentalDetails.endRentingPhoto")}
              </Style.EquipmentInfoTitle>
              <Style.Photo
                src={rentalDetails?.endPhotoPathSecond || ""}
                alt="Rental End Photo"
                onClick={() => handleOpenChildModal(rentalDetails?.endPhotoPathSecond)}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null; // prevents looping
                  currentTarget.src = NoImagePlaceholder;
                }}
              />
            </Style.PhotoWrapper>
          </Style.PhotosContainer>
        </TabPanel>
        <Style.RentalLogsContainer>
          <Style.RentalLogsTitle>
            {t("modal.rentalDetails.rentalLogs.title")}
          </Style.RentalLogsTitle>
          <Style.RentalLogsContentContainer>
            {rentalLogsDetails &&
              rentalLogsDetails.map((rentalLog) => {
                return (
                  <>
                    <Style.RentalLogsContent>
                      <Style.RentalLogDetail>
                        {t(`audit.rentals.status.${rentalLog.status}`)}
                      </Style.RentalLogDetail>
                      <Style.RentalLogDetail>
                        {dayjs(rentalLog.creationDate).format("HH:mm:ss")}
                      </Style.RentalLogDetail>
                    </Style.RentalLogsContent>
                    <Style.Bar />
                  </>
                );
              })}
          </Style.RentalLogsContentContainer>
        </Style.RentalLogsContainer>
      </Style.ModalContainer>
  );
};

export default RentalDetails;
